/**
 * 审批中心 API（APC · corporate-api §4.9，后缀 /merchant/api/v1/approvals/*）。
 *
 * 只读聚合查询两端点（APC-001 列表 / APC-002 角标计数）+ KYC 决策转发封装
 * （sprint-contract §3.1-D7：既有后端端点 API-KYC-008
 * `POST /merchant/api/v1/kyc/cases/{caseId}/review` 的 merchantPost 薄封装，
 * 入参 sourceId=caseId；该封装为合同唯一允许的新封装，不算新造决策 URL。
 * 禁用 kyc.ts 的 reviewKycCase——PUT /cardholders/{id}/kyc-review 持卡人链路，语义不符）。
 *
 * 其余类型决策一律复用既有函数（corporate.ts）：decideApproval / decideCorporateExpense /
 * decideCorporateOnboarding，页面直调、此处不二次包装。
 *
 * 类型与后端 AggregatedApprovalItem / AggregatedPendingPage / PendingCountView 逐字段对齐：
 * 金额 decimal string、时间 ISO8601、expiresIn 剩余秒数字符串（null=无超时）、
 * items 含 D6 扩展 corpId/sourceId；响应体按 D8 允许为 PRD {items,total} 超集（page/pageSize 回显），
 * 对潜在缺失字段容错。
 */
import { merchantGet, merchantPost, type ApiResult } from './client';
import { buildPendingQuery, type ApprovalCenterListQuery } from './approvalsCore';

/** USE_MOCK 恒 false，mock 回调仅满足既有签名；误调用即显式失败（对齐 corporate.ts unsupportedMock） */
const unsupportedMock = (): never => {
  throw new Error('mock_unsupported: USE_MOCK=false');
};

// ================= DTO（对齐后端 domain 只读视图 record） =================

/** 五类审批类型（后端 ApprovalType 枚举名；CORP_CARD_ISSUE 本 Sprint 恒空集，D1） */
type AggregatedApprovalType =
  | 'CORP_TX_APPROVAL'
  | 'CORP_CARD_ISSUE'
  | 'CORP_EXPENSE'
  | 'ONBOARDING'
  | 'KYC';

/**
 * APC-001 聚合待办列表项（AggregatedApprovalItem 逐字段对齐 + D6 扩展）。
 * PRD 七字段：approvalType/approvalNo/summary/applicantMask/submittedAt/expiresIn/status；
 * D6 追加：corpId（企业单据详情跳转锚定）/ sourceId（转发决策路径参数）。
 */
export type AggregatedApprovalItem = {
  approvalType: string | null;
  approvalNo: string | null;
  /** 后端拼装摘要：消费审批=金额+币种+MCC+商户名；报销=成员+金额+invoice 张数；开户/KYC=申请人掩码+申请类型 */
  summary: string | null;
  /** 个人申请=申请人姓名掩码；企业单据=企业名+成员名（掩码透传，禁明文敏感信息） */
  applicantMask: string | null;
  /** 各域单据创建时间（ISO8601） */
  submittedAt: string | null;
  /** 距 expire_at 剩余秒数的整数字符串（如 "780"）；无超时类型 null（D5） */
  expiresIn: string | null;
  /** 恒 PENDING_APPROVAL（本列表唯一可见状态） */
  status: string | null;
  /** D6 扩展：企业单据归属企业 ID（CORP_TX_APPROVAL/CORP_EXPENSE 详情跳转锚定） */
  corpId?: string | null;
  /** D6 扩展：各域单据 ID（转发决策路径参数 / KYC caseId / ONBOARDING applicationId） */
  sourceId?: string | null;
};

/** APC-001 分页出参（PRD 契约 {items,total} 的 D8 允许超集：追加 page/pageSize 回显；total long → string） */
type AggregatedPendingPage = {
  total: string;
  items: AggregatedApprovalItem[];
  page?: number;
  pageSize?: number;
};

/** APC-002 出参（PendingCountView）：byType 五键恒齐（CORP_CARD_ISSUE 恒 0，D1） */
type PendingCountView = {
  total: string;
  byType: Partial<Record<AggregatedApprovalType, string>> & Record<string, string | undefined>;
};

// ================= APC 只读查询 =================

/** APC-001 聚合待办列表（approvalType 多值 / from / to / page / pageSize；排序以服务端返回序为准）。 */
export function listPendingApprovals(
  query: ApprovalCenterListQuery = {},
): Promise<ApiResult<AggregatedPendingPage>> {
  const qs = buildPendingQuery(query);
  return merchantGet<AggregatedPendingPage>(
    `/approvals/pending${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

/** APC-002 待办计数（侧栏角标；byType 五键恒齐，前端 approvalBadgeCount 聚合容错）。 */
export function fetchPendingApprovalCount(): Promise<ApiResult<PendingCountView>> {
  return merchantGet<PendingCountView>('/approvals/pending/count', () =>
    ({ total: '0', byType: {} }),
  );
}

// ================= KYC 决策转发（D7 唯一新封装） =================

/** KYC 决策词（审批中心只提供通过/拒绝；补件/转平台去原页 PAGE-MCH-USER-002 深处理） */
type KycCaseDecision = 'APPROVE' | 'REJECT';

/** API-KYC-008 决策 body（对齐后端 KycReviewRequest：decision 必填，REJECT reason 必填由页面校验） */
type KycCaseReviewBody = {
  decision: KycCaseDecision;
  reason?: string;
};

/** API-KYC-008 决策结果（最小字段集；后端 KycReviewResult 原文透传其余键） */
type KycCaseReviewResult = {
  caseId: string | null;
  status: string | null;
};

/**
 * API-KYC-008 商户 KYC 案件一审（既有后端端点薄封装，sprint-contract §3.1-D7）：
 * POST /merchant/api/v1/kyc/cases/{caseId}/review，入参 sourceId=caseId。
 * Idempotency-Key 由 client.ts 恒注入（opts.idempotencyKey 复用同键支持二次确认重放）。
 */
export function decideKycCase(
  caseId: string,
  body: KycCaseReviewBody,
  opts: { idempotencyKey?: string } = {},
): Promise<ApiResult<KycCaseReviewResult>> {
  return merchantPost<KycCaseReviewResult>(
    `/kyc/cases/${encodeURIComponent(caseId)}/review`,
    body,
    unsupportedMock,
    { idempotencyKey: opts.idempotencyKey },
  );
}
